/**
 * Resolución de base URL según el estado de conexión.
 *
 * El front tiene un modo offline: cuando el navegador pierde conexión, las llamadas se
 * redirigen a un backend local (VUE_APP_BASE_URL_OFFLINE, por defecto localhost:4000)
 * en lugar del backend remoto. Ese swap vivía SOLO en el interceptor de axios global de
 * main.js, así que la instancia central `http.js` — la única que manda el token — no lo
 * tenía. Migrar llamadas de axios crudo a http.js les sacaba el modo offline.
 *
 * Este módulo es la única implementación del swap; main.js y http.js la comparten.
 *
 * Las base URL se leen de forma perezosa (no a nivel de módulo) para que sean
 * observables desde tests. En build, webpack las reemplaza literalmente igual.
 */
import networkMonitor from '@/services/networkMonitor';

const normalizarBaseUrl = (baseUrl) => {
  if (!baseUrl) return null;
  return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
};

export const baseUrlOnline = () => normalizarBaseUrl(process.env.VUE_APP_BASE_URL);

export const baseUrlOffline = () =>
  normalizarBaseUrl(process.env.VUE_APP_BASE_URL_OFFLINE || 'http://localhost:4000/');

/**
 * Estado de conexión. Prefiere networkMonitor (que escucha los eventos online/offline
 * del navegador) y cae a navigator.onLine si el servicio todavía no se inicializó.
 * @returns {boolean}
 */
export const hayConexion = () =>
  typeof networkMonitor?.isOnline === 'boolean' ? networkMonitor.isOnline : navigator.onLine;

/**
 * Base URL que corresponde al estado de conexión actual.
 * Para instancias de axios creadas con `baseURL` (http.js).
 * @returns {string|null}
 */
export const resolverBaseUrl = () => {
  const online = baseUrlOnline();
  const offline = baseUrlOffline();

  // Sin ambas configuradas no hay swap posible — mismo guard que el interceptor original.
  if (!online || !offline) return online;

  return hayConexion() ? online : offline;
};

/**
 * Reescribe una URL absoluta al backend que corresponde al estado de conexión actual.
 * Para el interceptor global de axios, donde `config.url` ya viene absoluta.
 * @param {string} url
 * @returns {string}
 */
export const resolverUrl = (url) => {
  if (!url || typeof url !== 'string') return url;

  const online = baseUrlOnline();
  const offline = baseUrlOffline();
  if (!online || !offline) return url;

  if (!hayConexion() && url.includes(online)) return url.replace(online, offline);
  if (hayConexion() && url.includes(offline)) return url.replace(offline, online);

  return url;
};
